import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Offcanvas Component
 *
 * Sidebar panel that slides in from the side of the screen.
 * Based on Preline UI offcanvas patterns.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether offcanvas is open
 * @param {Function} props.onClose - Close handler
 * @param {string|React.ReactNode} props.title - Offcanvas title (string or ReactNode for custom content)
 * @param {React.ReactNode} props.children - Offcanvas content
 * @param {string} props.side - Which side to slide from ('left' or 'right')
 * @param {string} props.size - Size of offcanvas ('sm', 'md', 'lg', 'full')
 */
export function Offcanvas({
  isOpen,
  onClose,
  title,
  children,
  side = 'right',
  size = 'md'
}) {
  const offcanvasRef = useRef(null);
  const previousFocusRef = useRef(null);
  const hasOpenedOnce = useRef(false);

  // Size classes
  const sizeClasses = {
    sm: 'max-w-xs',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full'
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      hasOpenedOnce.current = true;
      document.addEventListener('keydown', handleEscape);
      // Prevent body and html scroll
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      // Add padding to prevent layout shift from scrollbar removal
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      // Save current focus
      previousFocusRef.current = document.activeElement;

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        // Restore focus
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
        }
      };
    }
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Ensure document.body exists before creating portal
  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  const offcanvasContent = (
    <div
      className={`fixed inset-0 z-[60] overflow-hidden transition-all duration-300 ${
        isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="offcanvas-title"
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      ></div>

      {/* Offcanvas Panel */}
      <div
        className={`
          fixed top-0 bottom-0 flex flex-col w-full bg-white shadow-xl
          ${side === 'right' ? 'right-0' : 'left-0'}
          ${sizeClasses[size]}
          transform ${hasOpenedOnce.current && isOpen ? '' : 'transition-transform duration-300 ease-in-out'}
          ${isOpen ? 'translate-x-0' : (side === 'right' ? 'translate-x-full' : '-translate-x-full')}
        `}
        ref={offcanvasRef}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start py-3 px-4 border-b border-border-subtle">
          <h3 id="offcanvas-title" className="font-semibold text-default">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-shrink-0 justify-center items-center size-8 rounded-lg text-muted hover:text-default focus:outline-none focus:ring-2 focus:ring-focus-ring-subtle transition-colors cursor-pointer"
            aria-label="Close"
          >
            <Icon icon="lucide:x" className="size-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(offcanvasContent, document.body);
}

Offcanvas.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  children: PropTypes.node.isRequired,
  side: PropTypes.oneOf(['left', 'right']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full']),
};
